import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, bullhornApi, tenantsApi, getErrorMessage } from '../services/api';
import UsageBadge from '../components/UsageBadge';
import { ChartCard, DashCard, KpiCard } from '../components/dashboard/DashCard';
import WindowSelector, { WINDOW_OPTIONS, readStoredWindow, storeWindow } from '../components/dashboard/WindowSelector';
import ThemeToggle, { readStoredTheme, storeTheme } from '../components/dashboard/ThemeToggle';
import InsightsTable from '../components/dashboard/InsightsTable';
import { formatDuration } from '../utils/duration';
import { toast } from 'sonner';
import {
  FileText, Send, CheckCircle2, Eye, RefreshCw, Link2,
  TrendingUp, Timer, AlertCircle,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

// Recharts is ~120 kB gzipped; load the charts only when the dashboard opens so
// every other page (notably the candidate completion page) stays lighter.
const CompletionsChart = lazy(() => import('../components/dashboard/CompletionsChart'));
const MedianByTemplateChart = lazy(() => import('../components/dashboard/MedianByTemplateChart'));
const PipelineChart = lazy(() => import('../components/dashboard/PipelineChart'));
const PacketProgressChart = lazy(() => import('../components/dashboard/PacketProgressChart'));

/**
 * UnifiedDashboardPage — the compliance manager's analytics home for documents.
 *
 * A time-window selector drives the KPI row (Total / Sent / Opened / Completed /
 * Completion rate / Median completion time), the charts (completions over time,
 * document pipeline, packet progress, median completion time by template) and
 * the per-template insights table. All of that comes from one server-aggregated,
 * cached call: GET /api/dashboard/metrics. The activity feed is a separate
 * light, uncached call: GET /api/dashboard/recent.
 *
 * Methodology lives in the worker (services/dashboardMetrics.ts): cumulative
 * funnel counts, median open -> submit, minimum sample before any figure, and a
 * packet's state judged across every document in one send.
 */

const STATUS_LABELS = {
  sent: 'Sent', opened: 'Opened', in_progress: 'In progress',
  completed: 'Completed', expired: 'Expired', cancelled: 'Cancelled',
  pending_approval: 'Pending approval', approved: 'Approved',
};

const windowLabel = (key) => WINDOW_OPTIONS.find((o) => o.value === key)?.label ?? key;

export default function UnifiedDashboardPage() {
  const navigate = useNavigate();
  const { tenant, getTenantPath } = useAuth();
  const docsEnabled = tenant?.settings?.enable_documents !== false;

  const [recent, setRecent] = useState([]);
  const [bhConnected, setBhConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [theme, setTheme] = useState(readStoredTheme);
  const [windowKey, setWindowKey] = useState(readStoredWindow);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(false);
  const metricsRequest = useRef(0);

  const loadMetrics = useCallback(async (key, { fresh = false } = {}) => {
    const mine = ++metricsRequest.current;
    setMetricsLoading(true);
    try {
      const res = await dashboardApi.getMetrics(key, { fresh });
      if (mine !== metricsRequest.current) return; // a newer window was picked meanwhile
      setMetrics(res.data);
      setMetricsError(false);
    } catch (err) {
      if (mine !== metricsRequest.current) return;
      setMetricsError(true);
      toast.error(getErrorMessage(err, 'Failed to load document metrics'));
    } finally {
      if (mine === metricsRequest.current) setMetricsLoading(false);
    }
  }, []);

  // The Bullhorn chip is checked in the background: asking Bullhorn (and, if it's down, trying to
  // reconnect) can take far longer than everything else on this page, and used to hold the whole
  // dashboard on a spinner until it answered.
  const checkBullhorn = useCallback(async () => {
    // A workspace that has never set Bullhorn up has nothing to check.
    if (tenant?.bullhorn_config?.configured === false) { setBhConnected(false); return; }
    try {
      const bhRes = await bullhornApi.status().catch(() => ({ data: { connected: false } }));
      setBhConnected(!!bhRes.data?.connected);

      // Best-effort Bullhorn auto-connect when the chip is red.
      if (!bhRes.data?.connected && tenant?.id) {
        try {
          const auto = await tenantsApi.autoConnectBullhorn(tenant.id);
          if (auto.data?.auto_connected) setBhConnected(true);
        } catch (_e) { console.warn('Bullhorn auto-connect skipped:', _e?.message || _e); }
      }
    } catch (_e) {
      setBhConnected(false);
    }
  }, [tenant?.id, tenant?.bullhorn_config?.configured]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const recentRes = docsEnabled ? await dashboardApi.getRecent(10) : { data: [] };
      setRecent(recentRes.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    checkBullhorn();
  }, [docsEnabled, checkBullhorn]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (docsEnabled) loadMetrics(windowKey); }, [docsEnabled, windowKey, loadMetrics]);

  const refreshAll = () => {
    load();
    if (docsEnabled) loadMetrics(windowKey, { fresh: true });
  };
  const changeWindow = (key) => { storeWindow(key); setWindowKey(key); };
  const toggleTheme = () => setTheme((t) => { const next = t === 'dark' ? 'light' : 'dark'; storeTheme(next); return next; });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const k = metrics?.kpis;
  const minSample = metrics?.min_sample ?? 5;
  const awaiting = metrics == null && !metricsError; // first load of the metrics
  const dark = theme === 'dark';
  const updatedAt = metrics?.generated_at ? format(new Date(metrics.generated_at), 'HH:mm') : null;

  const medianValue = k?.median_completion_seconds != null ? formatDuration(k.median_completion_seconds) : 'Not enough data';
  const medianCaption = !k ? null
    : k.median_completion_seconds != null
      ? `based on ${k.median_sample_size} ${k.median_sample_size === 1 ? 'completion' : 'completions'}`
      : `${k.median_sample_size} of ${minSample} completions needed`;

  return (
    <DashboardLayout>
      <div className="sm:p-4 lg:p-8 max-w-7xl mx-auto">
        <div
          className={`dash-root ${dark ? 'dash-dark' : ''} rounded-2xl border p-3 sm:p-6 lg:p-8 space-y-6`}
          style={{ borderColor: dark ? 'var(--dash-border)' : 'transparent' }}
          data-testid="unified-dashboard-page"
        >
          {/* ============== HEADER ============== */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold tracking-tight" style={{ color: 'var(--dash-text)' }}>Dashboard</h1>
              <p className="dash-muted mt-1">How your documents and packets are performing.</p>
              <div className="mt-3 flex flex-wrap items-center gap-2 sm:gap-3">
                <UsageBadge />
                <BullhornChip connected={bhConnected} onConnect={async () => {
                  try {
                    await bullhornApi.authenticate();
                    setBhConnected(true);
                    toast.success('Connected to Bullhorn');
                  } catch (_) {
                    toast.error('Failed to connect to Bullhorn');
                  }
                }} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ThemeToggle theme={theme} onToggle={toggleTheme} />
              <button
                type="button"
                className="dash-btn"
                onClick={refreshAll}
                disabled={refreshing || metricsLoading}
                data-testid="dashboard-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing || metricsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {docsEnabled && (
                <button
                  type="button"
                  className="dash-btn dash-btn-primary"
                  onClick={() => navigate(getTenantPath('/send'))}
                  data-testid="dashboard-send-btn"
                >
                  <Send className="w-3.5 h-3.5" />
                  Send a document
                </button>
              )}
            </div>
          </div>

          {!docsEnabled ? (
            <DashCard className="p-8 text-center" data-testid="dashboard-docs-disabled">
              <p className="text-sm font-medium" style={{ color: 'var(--dash-text)' }}>Documents are switched off for this workspace.</p>
              <p className="dash-caption mt-1">Turn them on in Settings to see document and packet analytics here.</p>
            </DashCard>
          ) : (
            <section className="space-y-4" data-testid="dashboard-documents-section" aria-busy={metricsLoading}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <WindowSelector value={windowKey} onChange={changeWindow} />
                <p className="dash-caption" data-testid="dashboard-window-caption">
                  {windowKey === 'all'
                    ? 'All documents ever sent. Times cover everything completed.'
                    : `Documents sent in the last ${windowLabel(windowKey)}. Times cover documents completed in that period.`}
                  {updatedAt && ` Updated ${updatedAt}.`}
                </p>
              </div>

              {metricsError && !metrics ? (
                <DashCard className="p-6 flex flex-wrap items-center gap-3" data-testid="dashboard-metrics-error">
                  <AlertCircle className="w-5 h-5" style={{ color: 'var(--dash-accent-text)' }} />
                  <p className="text-sm flex-1" style={{ color: 'var(--dash-text)' }}>We couldn&apos;t load the document metrics.</p>
                  <button type="button" className="dash-btn" onClick={() => loadMetrics(windowKey, { fresh: true })}>Try again</button>
                </DashCard>
              ) : (
                <div
                  className="space-y-4"
                  style={{ opacity: metricsLoading && metrics ? 0.55 : 1, transition: 'opacity 150ms ease' }}
                >
                  {/* KPI row */}
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
                    <KpiCard
                      icon={FileText}
                      label="Total documents"
                      value={k?.total ?? 0}
                      loading={awaiting}
                      caption={k?.delivered_pdf ? `incl. ${k.delivered_pdf} delivered as PDF` : 'sent in this period'}
                      onClick={() => navigate(getTenantPath('/documents'))}
                      testId="stat-doc-total"
                    />
                    <KpiCard
                      icon={Send}
                      label="Sent"
                      value={k?.sent ?? 0}
                      loading={awaiting}
                      caption="needing candidate action"
                      testId="stat-doc-sent"
                    />
                    <KpiCard
                      icon={Eye}
                      label="Opened"
                      value={k?.opened ?? 0}
                      loading={awaiting}
                      caption={k?.sent ? `${Math.round((k.opened / k.sent) * 100)}% of sent` : null}
                      testId="stat-doc-opened"
                    />
                    <KpiCard
                      icon={CheckCircle2}
                      label="Completed"
                      value={k?.completed ?? 0}
                      loading={awaiting}
                      caption="by candidates"
                      onClick={() => navigate(getTenantPath('/documents?status=completed'))}
                      testId="stat-doc-completed"
                    />
                    <KpiCard
                      icon={TrendingUp}
                      label="Completion rate"
                      value={k?.completion_rate_pct != null ? `${k.completion_rate_pct}%` : '—'}
                      loading={awaiting}
                      caption={k?.sent ? `${k.completed} of ${k.sent} sent` : 'nothing sent yet'}
                      testId="stat-doc-completion-rate"
                    />
                    <KpiCard
                      icon={Timer}
                      label="Median completion time"
                      value={medianValue}
                      loading={awaiting}
                      caption={medianCaption}
                      testId="stat-doc-median-time"
                    />
                  </div>

                  {/* Throughput + pipeline */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    <ChartCard
                      className="lg:col-span-3"
                      title="Completions over time"
                      subtitle="Documents completed by candidates"
                      testId="card-completions"
                    >
                      {awaiting ? <ChartSkeleton /> : <Suspense fallback={<ChartSkeleton />}><CompletionsChart series={metrics?.completions_over_time} /></Suspense>}
                    </ChartCard>
                    <ChartCard className="lg:col-span-2" title="Document pipeline" subtitle="Where each document sent in this period stands now" testId="card-pipeline">
                      {awaiting ? <ChartSkeleton /> : <Suspense fallback={<ChartSkeleton />}><PipelineChart pipeline={metrics?.pipeline} /></Suspense>}
                    </ChartCard>
                  </div>

                  <ChartCard
                    title="Packet progress"
                    subtitle="Packets sent in this period, by how far each recipient has got"
                    testId="card-packets"
                  >
                    {awaiting ? <ChartSkeleton /> : <Suspense fallback={<ChartSkeleton />}><PacketProgressChart packets={metrics?.packets} /></Suspense>}
                  </ChartCard>

                  <ChartCard
                    title="Median completion time by template"
                    subtitle="Spot the slow documents — time from first open to submit"
                    testId="card-median-by-template"
                  >
                    {awaiting ? <ChartSkeleton /> : <Suspense fallback={<ChartSkeleton />}><MedianByTemplateChart templates={metrics?.templates} minSample={minSample} /></Suspense>}
                  </ChartCard>

                  <ChartCard
                    title="Completion insights"
                    subtitle="Per template — click a heading to sort"
                    testId="card-completion-insights"
                  >
                    {awaiting ? <ChartSkeleton /> : <InsightsTable templates={metrics?.templates} minSample={minSample} />}
                  </ChartCard>
                </div>
              )}
            </section>
          )}

          {/* ============== RECENT ACTIVITY ============== */}
          {docsEnabled && (
            <DashCard className="p-5 sm:p-6" data-testid="dashboard-recent-activity">
              <div className="flex flex-wrap items-center justify-between gap-1 mb-4">
                <h3 className="font-heading text-lg font-semibold" style={{ color: 'var(--dash-text)' }}>Recent activity</h3>
                <p className="dash-caption">Latest {recent.length || 10} documents sent</p>
              </div>
              {recent.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm dash-muted">No activity yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recent.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => navigate(getTenantPath('/documents'))}
                      className="dash-row w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left"
                      data-testid={`dashboard-activity-doc-${d.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="dash-icon-chip flex-shrink-0" style={{ borderRadius: '9999px' }} aria-hidden="true">
                          <FileText className="w-4 h-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate" style={{ color: 'var(--dash-text)' }}>{d.candidate_name}</p>
                          <p className="text-xs dash-muted truncate">
                            {d.template_name}{d.packet_name ? ` · ${d.packet_name}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <StatusPill status={d.status} />
                        <span className="text-xs dash-muted w-24 text-right hidden sm:block">
                          {d.sent_at ? formatRelative(d.sent_at) : '—'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </DashCard>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

/* ---------- Helpers ---------- */

function ChartSkeleton() {
  return <div className="dash-skeleton w-full" style={{ height: 220 }} aria-hidden="true" />;
}

function StatusPill({ status }) {
  const done = status === 'completed' || status === 'approved';
  return (
    <span className={`dash-pill ${done ? 'dash-pill-accent' : ''}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function BullhornChip({ connected, onConnect }) {
  const chip = { padding: '0.375rem 0.75rem', fontSize: '0.75rem', textTransform: 'none', letterSpacing: 0 };
  if (connected) {
    return (
      <span className="dash-pill inline-flex items-center gap-2" style={chip}>
        <span className="w-2 h-2 rounded-full" style={{ background: '#2F9E6E' }} aria-hidden="true" />
        <span className="font-medium" data-testid="bullhorn-connected-badge">Bullhorn connected</span>
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="dash-pill inline-flex items-center gap-2" style={chip}>
        <span className="w-2 h-2 rounded-full" style={{ background: '#D14B3A' }} aria-hidden="true" />
        <span className="font-medium" data-testid="bullhorn-disconnected-badge">Bullhorn not connected</span>
      </span>
      <button type="button" className="dash-btn" onClick={onConnect} data-testid="connect-bullhorn-button">
        <Link2 className="w-3.5 h-3.5" />
        Connect
      </button>
    </div>
  );
}

function formatRelative(ts) {
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true });
  } catch {
    return format(new Date(ts), 'MMM d');
  }
}
